/**
 * Video playback manager — poster-first, one-at-a-time, play-on-view.
 *
 * - Peeks: pause only; poster/img shows (no seek / no reload flicker)
 * - Primary: preload=auto, resume from ashResumeAt, play when buffered
 * - Never call load() if media already attached (keeps currentTime)
 */
(function () {
  if (window.__ashVideoLazyInit) return;
  window.__ashVideoLazyInit = true;

  var MAX_PLAYING = 1;
  var HLS_CDN =
    window.__ashHlsUrl || 'https://cdn.jsdelivr.net/npm/hls.js@1.5.18/dist/hls.min.js';
  var playing = [];
  var syncTimer = null;
  var syncQueued = false;
  var observed = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
  var hlsLoadPromise = null;
  var prefetchedPosters = typeof Set !== 'undefined' ? new Set() : null;

  function findHlsUrl(video) {
    var nodes = video.querySelectorAll('source');
    for (var i = 0; i < nodes.length; i++) {
      var src = nodes[i].getAttribute('src') || nodes[i].dataset.ashSrc || '';
      var type = (nodes[i].getAttribute('type') || '').toLowerCase();
      if (/\.m3u8(\?|$)/i.test(src) || type.indexOf('mpegurl') !== -1) return src;
    }
    return '';
  }

  function findLightMp4Url(video) {
    var nodes = video.querySelectorAll('source');
    var best = '';
    var bestScore = 99;
    for (var i = 0; i < nodes.length; i++) {
      var src = nodes[i].getAttribute('src') || nodes[i].dataset.ashSrc || '';
      if (!/\.mp4(\?|$)/i.test(src)) continue;
      var score = 5;
      if (/7\.2Mbps/i.test(src)) score = 9;
      else if (/4\.8Mbps|3\.3Mbps/i.test(src)) score = 4;
      else if (/3\.0Mbps|2\.5Mbps|720p/i.test(src)) score = 3;
      else if (/1\.2Mbps|480p/i.test(src)) score = 2;
      if (score < bestScore) {
        bestScore = score;
        best = src;
      }
    }
    return bestScore <= 4 ? best : best;
  }

  function supportsNativeHls() {
    try {
      var probe = document.createElement('video');
      return probe.canPlayType('application/vnd.apple.mpegurl') !== '';
    } catch (e) {
      return false;
    }
  }

  function loadHlsLibrary() {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (hlsLoadPromise) return hlsLoadPromise;
    hlsLoadPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = HLS_CDN;
      s.async = true;
      s.onload = function () {
        if (window.Hls) resolve(window.Hls);
        else reject(new Error('Hls missing'));
      };
      s.onerror = function () {
        reject(new Error('Hls load failed'));
      };
      document.head.appendChild(s);
    });
    return hlsLoadPromise;
  }

  function pickHlsStartLevel(hls) {
    var levels = hls.levels || [];
    var pick = -1;
    for (var i = 0; i < levels.length; i++) {
      var h = levels[i].height || 0;
      var br = levels[i].bitrate || 0;
      if (h && h <= 720) pick = i;
      else if (pick < 0 && br && br <= 3500000) pick = i;
    }
    if (pick < 0 && levels.length > 1) pick = Math.min(1, levels.length - 1);
    if (pick >= 0) {
      try {
        hls.startLevel = pick;
        hls.loadLevel = pick;
        hls.nextLevel = pick;
        hls.currentLevel = pick;
      } catch (e) {}
    }
  }

  function ensureAdaptive(video, done) {
    if (video.dataset.ashMp4Only === '1') {
      done(false);
      return;
    }
    // Safari already plays HLS natively from <source> — skip hls.js.
    if (video.classList.contains('slide__video') && supportsNativeHls()) {
      done(false);
      return;
    }
    var url = findHlsUrl(video);
    if (!url) {
      done(false);
      return;
    }
    if (video.dataset.ashHls === '1' && video.__ashHls) {
      done(true);
      return;
    }

    loadHlsLibrary()
      .then(function (Hls) {
        if (Hls && Hls.isSupported()) {
          if (video.__ashHls) {
            try {
              video.__ashHls.destroy();
            } catch (e) {}
            video.__ashHls = null;
          }
          var hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false,
            capLevelToPlayerSize: true,
            maxBufferLength: 30,
            maxMaxBufferLength: 60,
            abrEwmaDefaultEstimate: 800000,
            startLevel: -1,
          });
          hls.loadSource(url);
          hls.attachMedia(video);
          hls.on(Hls.Events.MANIFEST_PARSED, function () {
            pickHlsStartLevel(hls);
          });
          video.__ashHls = hls;
          video.dataset.ashHls = '1';
          done(true);
          return;
        }

        var light = findLightMp4Url(video);
        if (light && supportsNativeHls()) {
          video.querySelectorAll('source').forEach(function (s) {
            try {
              s.remove();
            } catch (e) {}
          });
          video.removeAttribute('src');
          var s = document.createElement('source');
          s.src = light;
          s.type = 'video/mp4';
          video.appendChild(s);
          try {
            video.load();
          } catch (e) {}
          done(true);
          return;
        }
        done(false);
      })
      .catch(function () {
        var light = findLightMp4Url(video);
        if (light) {
          video.querySelectorAll('source').forEach(function (s) {
            try {
              s.remove();
            } catch (e) {}
          });
          var s = document.createElement('source');
          s.src = light;
          s.type = 'video/mp4';
          video.appendChild(s);
          try {
            video.load();
          } catch (e) {}
          done(true);
          return;
        }
        done(false);
      });
  }

  function shouldTrack(video) {
    if (!video || video.tagName !== 'VIDEO') return false;
    if (video.closest('template')) return false;
    if (video.dataset.ashLazySkip === '1') return false;
    if (video.dataset.ashLazyVideo === '1') return false;
    if (video.closest('dialog')) return false;
    if (video.dataset.ashSourcesStashed === '1') return false;
    if (video.closest('[data-sl-clone]')) return false;
    return true;
  }

  function shouldManage(video) {
    if (!shouldTrack(video)) return false;
    if (!isDisplayed(video)) return false;
    return true;
  }

  /** Skip CSS-hidden desktop/mobile twin videos (they steal the play slot). */
  function isDisplayed(video) {
    if (!video) return false;
    var wrap = video.closest('.pdp-video-banner__wrap');
    if (wrap && wrap.getAttribute('aria-hidden') === 'true') return false;
    try {
      var style = window.getComputedStyle(video);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      var parent = video.parentElement;
      while (parent && parent !== document.body) {
        var ps = window.getComputedStyle(parent);
        if (ps.display === 'none' || ps.visibility === 'hidden') return false;
        parent = parent.parentElement;
      }
      var rect = video.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) return false;
    } catch (e) {}
    return true;
  }

  function visibleRatio(video) {
    var rect = video.getBoundingClientRect();
    if (!rect.width || !rect.height) return 0;
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var visibleW = Math.max(0, Math.min(rect.right, vw) - Math.max(rect.left, 0));
    var visibleH = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
    return Math.min(visibleW / rect.width, visibleH / rect.height);
  }

  function isMediaReady(video) {
    return !!(video.currentSrc || video.getAttribute('src') || video.querySelector('source[src]'));
  }

  function isGalleryVideo(video) {
    return !!video.closest('media-gallery') || video.dataset.ashGalleryVideo === '1';
  }

  function pickGalleryPrimaries() {
    var candidates = [];
    document.querySelectorAll('media-gallery video').forEach(function (v) {
      if (!shouldManage(v)) return;
      if (v.closest('slideshow-slide')) return;
      var r = visibleRatio(v);
      if (r > 0.2) candidates.push({ v: v, r: r });
    });
    candidates.sort(function (a, b) {
      return b.r - a.r;
    });
    document.querySelectorAll('media-gallery video').forEach(function (v) {
      if (v.closest('slideshow-slide')) return;
      v.dataset.ashGalleryPick = '0';
    });
    if (candidates.length) candidates[0].v.dataset.ashGalleryPick = '1';
  }

  /** @returns {'primary'|'preview'|'idle'} */
  function roleFor(video) {
    if (!isDisplayed(video)) return 'idle';

    var sl = video.closest('.sl-slide');
    if (sl) {
      if (sl.classList.contains('is-active') && !sl.hasAttribute('data-sl-clone')) {
        // Play only when the active collection slide is actually on-screen.
        return visibleRatio(video) >= 0.35 ? 'primary' : 'idle';
      }
      if (sl.hasAttribute('data-sl-clone')) return 'idle';
      return visibleRatio(video) > 0.15 ? 'preview' : 'idle';
    }

    var gallery = video.closest('media-gallery');
    if (gallery) {
      var gSlide = video.closest('slideshow-slide');
      if (gSlide) {
        var gRoot = gSlide.closest('slideshow-component');
        var gRatio = visibleRatio(video);
        if (gRoot && Array.isArray(gRoot.slides) && typeof gRoot.current === 'number') {
          if (gRoot.slides[gRoot.current] === gSlide && gRatio >= 0.3) return 'primary';
          return 'idle';
        }
        if (gSlide.getAttribute('aria-hidden') === 'false' && gRatio >= 0.3) return 'primary';
        // First gallery slide fallback before slideshow hydrates
        if (!gSlide.previousElementSibling && gRatio >= 0.3) return 'primary';
        return 'idle';
      }
      if (video.dataset.ashGalleryPick === '1' && visibleRatio(video) >= 0.3) return 'primary';
      return visibleRatio(video) > 0.15 ? 'preview' : 'idle';
    }

    var ss = video.closest('slideshow-slide');
    if (ss) {
      var root = ss.closest('slideshow-component');
      var ratioSs = visibleRatio(video);
      if (root && Array.isArray(root.slides) && typeof root.current === 'number') {
        // Current slide only plays while in viewport (avoids off-screen autoplay).
        if (root.slides[root.current] === ss && ratioSs >= 0.2) return 'primary';
        return 'idle';
      }
      if (ratioSs >= 0.45) return 'primary';
      if (ss.getAttribute('aria-hidden') === 'false' || ratioSs > 0) return 'preview';
      return 'idle';
    }

    var r = visibleRatio(video);
    if (r >= 0.5) return 'primary';
    if (r > 0) return 'preview';
    return 'idle';
  }

  function stashSources(video) {
    if (video.hasAttribute('src') && !video.dataset.ashSrc) {
      video.dataset.ashSrc = video.getAttribute('src');
    }
    video.querySelectorAll('source').forEach(function (source) {
      var src = source.getAttribute('src');
      var dataSrc = source.getAttribute('data-src');
      if (src && !source.dataset.ashSrc) source.dataset.ashSrc = src;
      else if (dataSrc && !source.dataset.ashSrc) source.dataset.ashSrc = dataSrc;
    });
  }

  function arm(video) {
    stashSources(video);
    if (isMediaReady(video)) return false;
    var changed = false;
    video.querySelectorAll('source').forEach(function (source) {
      var stored = source.dataset.ashSrc || source.getAttribute('data-src');
      if (stored && !source.getAttribute('src')) {
        source.setAttribute('src', stored);
        source.removeAttribute('data-src');
        changed = true;
      }
    });
    if (video.dataset.ashSrc && !video.getAttribute('src') && !video.querySelector('source[src]')) {
      video.setAttribute('src', video.dataset.ashSrc);
      changed = true;
    }
    if (changed) {
      try {
        video.load();
      } catch (e) {}
    }
    return changed;
  }

  function ensureWantPlay(video) {
    // Gallery / carousel / collection videos always want muted autoplay.
    if (
      video.closest('media-gallery') ||
      video.closest('.sl-slide') ||
      video.closest('.sl-collection-video') ||
      video.dataset.ashGalleryVideo === '1'
    ) {
      video.dataset.ashWantPlay = '1';
    } else if (!video.dataset.ashWantPlay) {
      video.dataset.ashWantPlay =
        video.hasAttribute('autoplay') || video.hasAttribute('loop') ? '1' : '0';
    }
    video.removeAttribute('autoplay');
  }

  function rememberTime(video) {
    try {
      if (video.currentTime > 0.15) {
        video.dataset.ashResumeAt = String(video.currentTime);
      }
    } catch (e) {}
  }

  function restoreTime(video) {
    var saved = parseFloat(video.dataset.ashResumeAt || '');
    if (!saved || isNaN(saved) || saved < 0.15) return;
    try {
      if (video.readyState >= 1 && Math.abs((video.currentTime || 0) - saved) > 0.4) {
        video.currentTime = saved;
      }
    } catch (e) {}
  }

  function removeFromPlaying(video) {
    var i = playing.indexOf(video);
    if (i !== -1) playing.splice(i, 1);
  }

  function enforceCap(next) {
    while (playing.length >= MAX_PLAYING) {
      var oldest = playing.shift();
      if (!oldest || oldest === next) continue;
      rememberTime(oldest);
      try {
        oldest.pause();
      } catch (e) {}
      try {
        oldest.preload = 'none';
      } catch (e) {}
      setPlayingVisual(oldest, false);
    }
  }

  function setPlayingVisual(video, isPlaying) {
    video.dataset.ashPlaying = isPlaying ? '1' : '0';
    var mediaHost = video.closest('.sl-media, .sl-collection-video, [data-ash-video-host]');
    if (mediaHost) {
      if (isPlaying) mediaHost.setAttribute('data-ash-playing', '1');
      else mediaHost.removeAttribute('data-ash-playing');
    }
    var host = video.closest('deferred-media');
    if (!host) return;
    var posterBtn = host.querySelector('.deferred-media__poster-button:first-of-type');
    if (!posterBtn) return;
    if (isPlaying) {
      host.setAttribute('data-media-loaded', 'true');
      posterBtn.classList.add('deferred-media__playing');
    } else {
      posterBtn.classList.remove('deferred-media__playing');
    }
  }

  /** Keep slideshow timer off while an advance-on-end hero video is current. */
  function pauseSlideshowForVideo(video) {
    if (!video || video.dataset.ashAdvanceOnEnd !== '1') return;
    var root = video.closest('slideshow-component');
    if (!root || root.closest('media-gallery')) return;
    if (typeof root.pause === 'function') {
      try {
        root.pause();
      } catch (e) {}
    }
  }

  /**
   * Slideshow.js calls resume() BEFORE initial select(), so the 4s autoplay
   * timer can steal the video slide before play ever starts (common on
   * mobile). Re-assert pause whenever the current slide is a hero video.
   */
  function pauseHeroSlideshowIfNeeded() {
    document.querySelectorAll('slideshow-component').forEach(function (root) {
      if (root.closest('media-gallery')) return;
      var slide = null;
      if (root.slides && typeof root.current === 'number') {
        slide = root.slides[root.current];
      }
      if (!slide) {
        slide = root.querySelector('slideshow-slide[aria-hidden="false"]:not([data-ash-clone])');
      }
      if (!slide || slide.hasAttribute('data-ash-clone')) return;
      var video = slide.querySelector('video.slide__video[data-ash-advance-on-end="1"], video[data-ash-advance-on-end="1"]');
      if (!video) return;
      if (typeof root.pause === 'function') {
        try {
          root.pause();
        } catch (e) {}
      }
    });
  }

  function pauseQuiet(video, asPreview) {
    ensureWantPlay(video);
    rememberTime(video);
    try {
      video.pause();
    } catch (e) {}
    removeFromPlaying(video);
    setPlayingVisual(video, false);
    try {
      video.preload = 'none';
    } catch (e) {}
    video.dataset.ashLazy = asPreview ? 'preview' : 'idle';
  }

  function minReadyState(video) {
    // Mobile Safari often stalls below HAVE_FUTURE_DATA — play as soon as
    // metadata/current frame exists, then let the buffer catch up.
    if (
      isGalleryVideo(video) ||
      video.closest('.sl-slide') ||
      video.closest('.sl-collection-video') ||
      video.closest('slideshow-slide') ||
      video.classList.contains('slide__video')
    ) {
      return 1;
    }
    return 2;
  }

  function startPlayback(video) {
    restoreTime(video);
    video.dataset.ashAllowPlay = '1';
    video.muted = true;
    video.defaultMuted = true;

    var onPlaying = function () {
      setPlayingVisual(video, true);
      pauseSlideshowForVideo(video);
      video.removeEventListener('playing', onPlaying);
    };
    video.addEventListener('playing', onPlaying);

    var attempt = function (triesLeft) {
      if (roleFor(video) !== 'primary') return;
      var p = video.play();
      if (p && typeof p.then === 'function') {
        p.then(function () {
          setPlayingVisual(video, true);
          pauseSlideshowForVideo(video);
        }).catch(function () {
          setPlayingVisual(video, false);
          if (triesLeft > 0) {
            setTimeout(function () {
              attempt(triesLeft - 1);
            }, 350);
          }
        });
      }
    };
    attempt(4);

    setTimeout(function () {
      video.dataset.ashAllowPlay = '0';
    }, 2000);
  }

  function waitThenPlay(video) {
    var need = minReadyState(video);
    if (video.readyState >= need) {
      startPlayback(video);
      return;
    }
    var onReady = function () {
      if (video.readyState < need && roleFor(video) === 'primary') return;
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('canplaythrough', onReady);
      video.removeEventListener('loadeddata', onReady);
      if (roleFor(video) !== 'primary') return;
      startPlayback(video);
    };
    video.addEventListener('canplay', onReady);
    video.addEventListener('canplaythrough', onReady);
    video.addEventListener('loadeddata', onReady);
    if (video.readyState >= need) onReady();
  }

  function playPrimary(video) {
    ensureWantPlay(video);
    if (video.dataset.ashWantPlay === '0') {
      pauseQuiet(video, true);
      return;
    }

    pauseSlideshowForVideo(video);

    if (!video.paused && playing.indexOf(video) !== -1) {
      video.dataset.ashLazy = 'ready';
      setPlayingVisual(video, true);
      return;
    }

    arm(video);
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    if (video.dataset.ashAdvanceOnEnd === '1') {
      video.removeAttribute('loop');
    } else if (!video.hasAttribute('loop')) {
      video.setAttribute('loop', '');
    }

    try {
      video.preload = 'auto';
    } catch (e) {}

    // softStop/init often leave preload=none with readyState 0. Setting
    // preload=auto alone does not restart resource selection — load() does.
    if (video.readyState < 1 && isMediaReady(video)) {
      try {
        video.load();
      } catch (e) {}
    }

    enforceCap(video);
    removeFromPlaying(video);
    playing.push(video);
    video.dataset.ashLazy = 'arming';

    var afterReady = function () {
      if (roleFor(video) !== 'primary') return;
      video.dataset.ashLazy = 'ready';
      waitThenPlay(video);
    };

    // Slideshow clips ship HLS+MP4 sources. On Chromium, attach hls.js
    // (or fall through to light MP4). Safari uses native HLS via sources.
    if (video.dataset.ashMp4Only === '1') {
      afterReady();
      return;
    }

    ensureAdaptive(video, afterReady);
  }

  function applyRole(video) {
    if (!shouldManage(video)) return;
    ensureWantPlay(video);
    var role = roleFor(video);
    var prev = video.dataset.ashLazy || '';

    if (role === 'primary') {
      playPrimary(video);
      return;
    }

    if (video.paused && (prev === 'preview' || prev === 'idle' || prev === 'arming') && role !== 'primary') {
      video.dataset.ashLazy = role === 'preview' ? 'preview' : 'idle';
      return;
    }

    pauseQuiet(video, role === 'preview');
  }

  function priorityScore(video) {
    // Visibility dominates. Small type bonuses so an active carousel
    // barely peeking in doesn't steal play from a fully-visible hero.
    var ratio = visibleRatio(video);
    var bonus = 0;
    if (video.closest('.sl-slide.is-active') && !video.closest('[data-sl-clone]')) {
      bonus = ratio >= 0.4 ? 35 : 5;
    } else if (video.closest('media-gallery')) {
      var gSlide = video.closest('slideshow-slide');
      if (gSlide) {
        var gRoot = gSlide.closest('slideshow-component');
        var isCurrent =
          (gRoot && Array.isArray(gRoot.slides) && gRoot.slides[gRoot.current] === gSlide) ||
          gSlide.getAttribute('aria-hidden') === 'false';
        bonus = isCurrent ? 30 : 10;
      } else if (video.dataset.ashGalleryPick === '1') {
        bonus = 30;
      } else {
        bonus = 10;
      }
    } else if (video.classList.contains('slide__video') || video.closest('slideshow-component')) {
      bonus = 25;
    }
    return ratio * 100 + bonus;
  }

  function scheduleSync() {
    if (syncQueued) return;
    syncQueued = true;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      syncQueued = false;
      pauseHeroSlideshowIfNeeded();
      pickGalleryPrimaries();
      var managed = [];
      document.querySelectorAll('video').forEach(function (v) {
        if (!shouldManage(v)) return;
        ensureWantPlay(v);
        managed.push({
          v: v,
          role: roleFor(v),
          score: priorityScore(v),
        });
      });

      var primaries = managed
        .filter(function (x) {
          return x.role === 'primary';
        })
        .sort(function (a, b) {
          return b.score - a.score;
        });
      var winner = primaries.length ? primaries[0].v : null;

      managed.forEach(function (x) {
        if (x.v === winner) {
          playPrimary(x.v);
          return;
        }
        if (x.role === 'preview') {
          // Don't yank a winner mid-play via preview of another section
          if (!x.v.paused && playing.indexOf(x.v) !== -1 && x.v !== winner) {
            pauseQuiet(x.v, true);
            return;
          }
          pauseQuiet(x.v, true);
          return;
        }
        pauseQuiet(x.v, false);
      });
    }, 120);
  }

  function fixBrokenPosterAttr(video) {
    var poster = video.getAttribute('poster') || '';
    if (!poster) return;
    if (poster.indexOf('://') !== -1 || poster.indexOf('//') === 0) return;
    if (poster.indexOf('/cdn/') === 0 || poster.indexOf('cdn.shopify') !== -1) return;
    video.removeAttribute('poster');
  }

  function cleanEmptySources(video) {
    video.querySelectorAll('source').forEach(function (s) {
      if (!s.getAttribute('src') && !s.dataset.ashSrc && !s.getAttribute('data-src')) {
        try {
          s.remove();
        } catch (e) {}
      }
    });
  }

  function prefetchPosterUrl(url) {
    if (!url) return;
    if (prefetchedPosters) {
      if (prefetchedPosters.has(url)) return;
      prefetchedPosters.add(url);
    }
    if (document.querySelector('link[data-ash-prefetch="' + url + '"]')) return;
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'image';
    link.href = url;
    link.dataset.ashPrefetch = url;
    document.head.appendChild(link);
  }

  function prefetchHeroNextSlide(root) {
    if (!root || !Array.isArray(root.slides) || typeof root.current !== 'number') return;
    if (root.closest('signature-carousel')) return;
    var nextIdx = (root.current + 1) % root.slides.length;
    var nextSlide = root.slides[nextIdx];
    if (!nextSlide) return;
    var img = nextSlide.querySelector(
      'img.sl-media__poster-img, .deferred-media__poster-image, img[src*="cdn.shopify"]'
    );
    if (img) prefetchPosterUrl(img.getAttribute('src') || img.currentSrc || '');
    var vid = nextSlide.querySelector('video[poster]');
    if (vid) prefetchPosterUrl(vid.getAttribute('poster') || '');
  }

  function initVideo(video) {
    // Track/observe even if currently CSS-hidden (desktop/mobile twin).
    // shouldManage still gates playback so the hidden twin never steals the slot.
    if (!shouldTrack(video)) return;
    if (observed) {
      if (observed.has(video)) return;
      observed.add(video);
    } else if (video.dataset.ashLazyObserved === '1') {
      return;
    } else {
      video.dataset.ashLazyObserved = '1';
    }
    ensureWantPlay(video);
    cleanEmptySources(video);
    stashSources(video);
    fixBrokenPosterAttr(video);
    video.removeAttribute('autoplay');
    // Keep metadata preload on slideshow / collection clips so mobile
    // can attach media; only starve far-below-fold generic videos.
    var keepMeta =
      video.closest('slideshow-slide') ||
      video.classList.contains('slide__video') ||
      video.closest('.sl-slide') ||
      video.closest('.sl-collection-video') ||
      isGalleryVideo(video);
    if (!keepMeta) {
      try {
        video.preload = 'none';
      } catch (e) {}
    }
    if (window.__ashVideoIO) window.__ashVideoIO.observe(video);
    if (shouldManage(video)) applyRole(video);
  }

  function start() {
    if ('IntersectionObserver' in window) {
      window.__ashVideoIO = new IntersectionObserver(
        function () {
          scheduleSync();
        },
        /* Fewer thresholds = fewer IO callbacks during scroll; still
           catches enter/mid/full visibility for MAX_PLAYING sync. */
        { rootMargin: '80px 0px', threshold: [0, 0.35, 0.85] }
      );
    }

    document.querySelectorAll('video').forEach(initVideo);

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var n = nodes[j];
          if (!n || n.nodeType !== 1) continue;
          if (n.tagName === 'VIDEO') initVideo(n);
          else if (n.querySelectorAll) n.querySelectorAll('video').forEach(initVideo);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('slideshow:select', function (e) {
      scheduleSync();
      var slide = e.detail && e.detail.slide;
      var root = slide && slide.closest && slide.closest('slideshow-component');
      prefetchHeroNextSlide(root);
      // Force gallery video play on active slide (mobile PDP)
      if (slide && slide.closest('media-gallery')) {
        var gv = slide.querySelector('video');
        if (gv) {
          gv.dataset.ashWantPlay = '1';
          setTimeout(function () {
            playPrimary(gv);
          }, 50);
        }
      }
    });

    document.addEventListener(
      'ended',
      function (e) {
        var video = e.target;
        if (!(video instanceof HTMLVideoElement)) return;
        if (video.dataset.ashAdvanceOnEnd !== '1') return;
        if (roleFor(video) !== 'primary') return;
        var slide = video.closest('slideshow-slide');
        var root = slide && slide.closest('slideshow-component');
        if (!root || typeof root.next !== 'function') return;
        try {
          root.pause();
        } catch (err) {}
        root.next();
      },
      true
    );

    document.addEventListener(
      'slideshow:select',
      function (e) {
        var slide = e.detail && e.detail.slide;
        var root = slide && slide.closest && slide.closest('slideshow-component');
        if (!root) return;
        var video = slide.querySelector('video[data-ash-advance-on-end="1"]');
        if (video) {
          if (typeof root.pause === 'function') {
            try {
              root.pause();
            } catch (err) {}
          }
        } else if (root.autoplay && typeof root.play === 'function') {
          try {
            root.paused = false;
            root.play();
          } catch (err) {}
        }
      },
      false
    );

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var t = mutations[i].target;
        if (t && t.classList && t.classList.contains('sl-slide')) {
          scheduleSync();
          return;
        }
      }
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });

    document.addEventListener(
      'play',
      function (e) {
        var video = e.target;
        if (!(video instanceof HTMLVideoElement) || !shouldManage(video)) return;
        if (video.dataset.ashAllowPlay === '1') return;
        if (roleFor(video) === 'primary') {
          // Keep highest-priority primary; pause lower-priority thieves
          var rivals = [];
          document.querySelectorAll('video').forEach(function (other) {
            if (other === video || !shouldManage(other)) return;
            if (roleFor(other) !== 'primary') return;
            rivals.push(other);
          });
          var best = video;
          var bestScore = priorityScore(video);
          rivals.forEach(function (other) {
            var s = priorityScore(other);
            if (s > bestScore) {
              best = other;
              bestScore = s;
            }
          });
          if (best !== video) {
            pauseQuiet(video, true);
            return;
          }
          enforceCap(video);
          if (playing.indexOf(video) === -1) playing.push(video);
          return;
        }
        pauseQuiet(video, true);
      },
      true
    );

    document.addEventListener(
      'pause',
      function (e) {
        var video = e.target;
        if (!(video instanceof HTMLVideoElement) || !shouldManage(video)) return;
        rememberTime(video);
        if (video.paused) setPlayingVisual(video, false);
      },
      true
    );

    document.addEventListener('shopify:section:load', scheduleSync);

    window.__ashVideoLazyArm = arm;
    window.__ashVideoLazyPlay = playPrimary;
    window.__ashVideoLazySync = scheduleSync;
    window.__ashVideoLazyPause = pauseQuiet;

    // Race with slideshow.js: it resume()s autoplay before initial select.
    // Keep re-pausing while the current slide is the hero video.
    pauseHeroSlideshowIfNeeded();
    [0, 60, 120, 250, 500, 1000, 2000, 4000].forEach(function (ms) {
      setTimeout(function () {
        pauseHeroSlideshowIfNeeded();
        scheduleSync();
      }, ms);
    });

    // iOS / Low Power Mode often blocks muted autoplay until a gesture.
    // Keep retrying on user gestures — a once-only unlock at y=0 misses
    // Signature Looks / CEO slides that become primary after scroll.
    var unlockPlayback = function () {
      pauseHeroSlideshowIfNeeded();
      document.querySelectorAll('video[data-ash-want-play="1"]').forEach(function (v) {
        if (!shouldManage(v) || roleFor(v) !== 'primary') return;
        if (!v.paused && playing.indexOf(v) !== -1) return;
        v.muted = true;
        v.defaultMuted = true;
        v.dataset.ashAllowPlay = '1';
        try {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } catch (err) {}
      });
      // Also nudge LCP heroes that skip the lazy manager.
      document.querySelectorAll('video[data-ash-lazy-skip="1"]').forEach(function (v) {
        if (!isDisplayed(v) || !v.paused) return;
        v.muted = true;
        try {
          var p2 = v.play();
          if (p2 && p2.catch) p2.catch(function () {});
        } catch (err2) {}
      });
      scheduleSync();
    };
    ['touchstart', 'pointerdown', 'click'].forEach(function (ev) {
      document.addEventListener(ev, unlockPlayback, { passive: true });
    });
    var scrollUnlockTimer = null;
    window.addEventListener(
      'scroll',
      function () {
        if (scrollUnlockTimer) return;
        scrollUnlockTimer = setTimeout(function () {
          scrollUnlockTimer = null;
          unlockPlayback();
        }, 150);
      },
      { passive: true }
    );

    setTimeout(scheduleSync, 500);
    setTimeout(scheduleSync, 1800);
    document.querySelectorAll('slideshow-component').forEach(prefetchHeroNextSlide);
  }

  /* Defer manager init slightly past first paint so LCP hero (hls.light
     + data-ash-lazy-skip) is not competing with full manager setup.
     Hero playback is independent; Signature Looks still arms on idle. */
  function scheduleStart() {
    var run = function () {
      start();
    };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run, { timeout: 1200 });
    } else {
      setTimeout(run, 0);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleStart);
  } else {
    scheduleStart();
  }
})();
